/**
 * AVA-660: the 360 mini map's agreed rules, and the names of the design tokens it reads. No imports,
 * so node tests load it directly. Spec: .claude/features/AVA-660_360_MINIMAP.md.
 *
 * Nothing else lives here on purpose:
 * - Sizes, icon/dot/marker sizes, stroke, cone opacity: design tokens (src/styles/_design-tokens.scss).
 * - Position: next to the 360 info button, measured at run time (PanoramaMiniMap.anchorToInfoButton).
 * - Click target: half the icon size. Click-vs-drag: CLICK_MOVE_TOLERANCE_PX (src/constants/pointer.js).
 * - Cone: as long as the 360's hotspot range, as wide as the panorama's field of view.
 */
export const MINI_MAP = Object.freeze({
  /** localStorage key remembering that the user hid the map (per device). */
  hiddenStorageKey: 'ava-mini-map-hidden',

  /** Radar zoom step of one wheel notch (the view's diameter is divided or multiplied by it). */
  wheelFactor: 1.15,

  /**
   * How strongly a scan is drawn, by where it is from the open 360 (Plan icons and Radar dots):
   * within the 360's hotspot range = full; beyond it on the same level, fading from `outOfRangeNear`
   * at the range to `outOfRangeFar` at `fadeEndRanges` × the range; on another level = `otherLevel`.
   */
  opacity: Object.freeze({ inRange: 1, outOfRangeNear: 0.85, outOfRangeFar: 0.5, fadeEndRanges: 3, otherLevel: 0.4 }),

  /**
   * Default zoom: show about `nearestScans` scans around the current one, so a dense deck (a scan
   * per m²) and a sparse site (a scan every 5 m) both open with a useful neighbourhood.
   */
  span: Object.freeze({ nearestScans: 12, minM: 6, maxM: 40 }),

  /** Design tokens the map reads (CSS custom properties). */
  tokens: Object.freeze({
    iconSize: '--ds-mini-map-icon-size',
    dotSize: '--ds-mini-map-dot-size',
    markerSize: '--ds-mini-map-marker-size',
    stroke: '--ds-mini-map-stroke',
    strokeStrong: '--ds-mini-map-stroke-strong',
    coneOpacity: '--ds-mini-map-cone-opacity',
    padding: '--ds-space-sm',
    gap: '--ds-space-sm',
  }),

  /** Radar colours, also design tokens (Vuetify theme + design system). */
  radarTokens: Object.freeze({
    background: '--ds-surface-elevated',
    scan: '--v-primary-base',
    accent: '--ds-color-pending',
    // Not --v-info-base: the theme sets info to the primary green, the same as the scan dots.
    you: '--v-accent-base',
    ring: '--ds-border-strong',
    // The reference point's X: not the cone's colour, which it usually sits inside.
    reference: '--ds-color-mixed',
  }),
});
